import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ProfileTab from './ProfileTab';
import AccountTab from './AccountTab';
import AppFieldsEditor from './AppFieldsEditor';
import VerificationDocs from './VerificationDocs';
import { visibleSections, SMOKE_FREE_STATEMENT } from '../../config/applicationFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ID_DOC_KEYS = ['police_check', 'passport', 'drivers_licence', 'national_id', 'seniors_concession_card', 'club_card'];

export const PROFILE_SUBTABS = [
  { key: 'identity', label: 'Personal Details' },
  { key: 'photos', label: 'Photos' },
  { key: 'id', label: 'ID & Verification' },
  { key: 'financial', label: 'Financial' },
  { key: 'lifestyle', label: 'Lifestyle' },
  { key: 'household', label: 'Household & Community' },
  { key: 'health', label: 'Health & Accessibility' },
  { key: 'safety', label: 'Safety & References' },
  { key: 'preferences', label: 'Preferences' },
  { key: 'compatibility', label: 'Compatibility' },
  { key: 'items', label: 'Useful Items' },
  { key: 'membership', label: 'Membership & Payment' },
  { key: 'settings', label: 'Notifications & Password' },
];

const ProfileAccount = ({ currentUser, setCurrentUser, appStatus, getAuthConfig, subTab, setSubTab }) => {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/applications/me`, getAuthConfig())
      .then(res => setApplication(res.data))
      .catch(() => setApplication(null))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = visibleSections(application);
  const sectionByKey = Object.fromEntries(sections.map(s => [s.key, s]));

  const renderAppSection = (key) => {
    if (loading) return <div style={{ fontSize: '14px', color: '#6b7280' }}>Loading your details…</div>;
    if (!application) return (
      <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '10px', padding: '16px', color: '#92400e', fontSize: '14px' }} data-testid="no-application-notice">
        No HSS Application on file for your account yet. Once your application is received, your answers appear here and can be edited.
      </div>
    );
    const section = sectionByKey[key];
    if (!section) return null;
    return <AppFieldsEditor section={section} application={application} onSaved={setApplication} getAuthConfig={getAuthConfig} />;
  };

  const panels = {
    identity: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <ProfileTab currentUser={currentUser} setCurrentUser={setCurrentUser} getAuthConfig={getAuthConfig} />
        {renderAppSection('personal')}
        {sectionByKey.partner && renderAppSection('partner')}
      </div>
    ),
    photos: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <VerificationDocs getAuthConfig={getAuthConfig} appStatus={appStatus} docKeys={['photo']}
          title="Member Photos"
          intro="Your portrait photos. Couples upload 4 photos (2 each). After approval you can set one as your public profile photo." />
      </div>
    ),
    id: (
      <VerificationDocs getAuthConfig={getAuthConfig} appStatus={appStatus} docKeys={ID_DOC_KEYS}
        title="ID & Verification"
        intro="Your police check and identity documents. Kept private and used only for member verification." />
    ),
    financial: renderAppSection('financial'),
    lifestyle: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 16px', fontSize: '14px', color: '#1e40af', fontWeight: 600 }} data-testid="smoke-free-statement">
          {SMOKE_FREE_STATEMENT}
        </div>
        {renderAppSection('lifestyle')}
      </div>
    ),
    household: renderAppSection('household'),
    health: renderAppSection('health'),
    safety: renderAppSection('safety'),
    preferences: renderAppSection('preferences'),
    compatibility: renderAppSection('compatibility'),
    items: renderAppSection('items'),
    membership: <AccountTab currentUser={currentUser} setCurrentUser={setCurrentUser} appStatus={appStatus} getAuthConfig={getAuthConfig} only="membership" />,
    settings: <AccountTab currentUser={currentUser} setCurrentUser={setCurrentUser} appStatus={appStatus} getAuthConfig={getAuthConfig} only="settings" />,
  };

  return (
    <div data-testid="profile-account-section">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', borderBottom: '2px solid #e5e7eb', marginBottom: '24px', paddingBottom: '2px' }} data-testid="profile-subtabs">
        {PROFILE_SUBTABS.filter(t => t.key !== 'partner').map(({ key, label }) => (
          <button key={key} onClick={() => setSubTab(key)} data-testid={`profile-subtab-${key}`}
            style={{
              border: 'none', background: subTab === key ? '#1a2332' : 'transparent',
              color: subTab === key ? 'white' : '#4b5563', fontWeight: subTab === key ? 700 : 500,
              fontSize: '13px', padding: '8px 14px', borderRadius: '8px 8px 0 0', cursor: 'pointer',
              transition: 'background-color 0.15s, color 0.15s'
            }}>
            {label}
          </button>
        ))}
      </div>
      {panels[subTab] || panels.identity}
    </div>
  );
};

export default ProfileAccount;
